import { zodResolver } from "@hookform/resolvers/zod";
import { IconCircleX, IconUserPlus } from "@tabler/icons-react";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Button, Col, Form, InputGroup, Offcanvas, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import TagsInput from "react-tagsinput";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { z } from "zod";
import { toast } from "sonner";
import { getCities, getCountries, getStates } from "../Utils/hooks/location";
import { GetRequest } from "../Utils/hooks/ApiHandler";
import { usePostRequest } from "../Utils/hooks/QueryHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { checkDuplicate, customStyles, getUpdatedFields, showServerError, useDebounce } from "../Utils/helpers/commonhelper";
import validator from "validator";
import { parsePhoneNumber } from "libphonenumber-js/min";
import {
  DateField,
  MultipleOpt,
  PhoneNumberInput,
  SelectOpt,
  Text,
  Time,
} from "./FieldComp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { CreateCompany } from "./CreateCompany";
import { UsersPopup } from "./UsersPopup";
import { ContactFormCommonOffcanvas } from "../Pages/Contacts/ContactModule/ContactFormCommonOffcanvas";
import useCommonData from "../Utils/actions/advanceFilteringAction";
const optionalUrl = (label) =>
  z
    .string()
    .trim()
    .optional()
    .refine(
      (val) => !val || !val.includes(" "),
      `${label} URL cannot contain spaces`
    )
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      `Invalid ${label} URL`
    );

const contactSchema = z.object({
  salutation: z.string().default("Mr.").optional().nullable(),
  first_name: z.string().min(1, "Required"),
  last_name: z.string().optional().nullable(),
  owner_id: z.string().optional(),

  visible_to: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        type: z.enum(["user", "team", "all"]),
      })
    )
    .nonempty("Required !"),

  report_to: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  countryId: z.string().optional(),
  state: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  zipcode: z.string().optional().nullable(),

  facebook: optionalUrl("Facebook").nullable(),
  twitter: optionalUrl("Twitter").nullable(),
  instagram: optionalUrl("Instagram").nullable(),

  linkedin: optionalUrl("Linkedin").nullable(),

  company_id: z.string().optional().nullable(),
  department: z.string().optional().nullable(),
  designation: z.string().optional().nullable(),
  lead_source_id: z.string().min(1, "Lead Source is required!"),
  note: z.string().optional().nullable(),
});


export const EditContactOffcanvas = ({
  show,
  onHide,
  setShow,
  contactId,
  refetch,
  allContacts,
  fetchContactDetails,
  setActiveCanvas,
}) => {

  // customer data from sesssion storage
  const [customer, setCustomer] = useLocalStorageState("customer");
  // token obj
  const headers = {
    Authorization: customer?.token.authorization,
    Authentication: customer?.token.authentication,
  };

  // master permission
  const [PERMISSION] = useLocalStorageState('permission')
  // for lead source permission
  const masterGrant = PERMISSION?.setting?.feature?.master;

  // loading state for for form submission
  const [loading, setLoading] = useState(false)
  const [isDataFetching, setIsDataFetching] = useState(false)
  const [contactDetails, setContactDetails] = useState(null)

  useEffect(() => {
    let isCurrent = true

    const loadContactDetails = async () => {
      if (!contactId) {
        setContactDetails(null)
        reset({})
        return
      }

      setContactDetails(null)
      reset({})
      setVisibleTo([])
      setEmails([{ email: "", email_type: "Office" }])
      setContacts([{ phone: "", phone_type: "Mobile" }])
      setTags([])
      setDecision(false)
      setImage(null)
      setIsDataFetching(true)
      try {
        const res = await GetRequest(
          `contacts/getContactDetailsById/${contactId}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        )
        if (isCurrent && res?.data?.[0]) {
          setContactDetails(res.data[0])
        } else if (isCurrent) {
          setIsDataFetching(false)
        }
      } catch (error) {
        if (isCurrent) {
          setContactDetails(null)
          setIsDataFetching(false)
        }
      }
    }

    loadContactDetails()
    return () => {
      isCurrent = false
    }
  }, [contactId])

  // function to set the dynamic fields for the dynamic fields
  const generateDynamicSchema = (fields) => {
    const schemaFields = {};
    fields.forEach((field) => {
      const fieldId = field.id;
      switch (field.label_type) {
        case "text":
        case "largeText":
        case "address":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;
          break;
        case "numerical":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string({ required_error: `${field.label_name} is required` })
              : z.string().optional();
          break;
        case "monetary":
          schemaFields[fieldId] = field.is_required === "yes"
            ? z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
              z.number({
                required_error: `${field.label_name} is required`,
                invalid_type_error: "Please enter a valid number"
              })
                .min(0.01, "Amount must be positive")
            )
            : z.preprocess(
              (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
              z.number().min(0, "Amount cannot be negative").optional().nullable()
            );
          break;
        case "single-option":
        case "user":
        case "person":
        case "organization":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;
        case "date":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;
        case "date range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z
                .object({
                  start: z.string().min(1, "Start date is required"),
                  end: z.string().min(1, "End date is required"),
                })
                .refine((data) => data.start <= data.end, {
                  message: "End date must be after start date",
                })
              : z
                .object({
                  start: z.string().optional(),
                  end: z.string().optional(),
                })
                .optional();
          break;
        case "time":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;
        case "time-range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.object({
                start: z.string().min(1, "Start time is required"),
                end: z.string().min(1, "End time is required"),
              })
              : z
                .object({
                  start: z.string().optional(),
                  end: z.string().optional(),
                })
                .optional();
          break;
        case "phone":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, "Phone number is required")
              : z.string().optional();
          break;
        case "multiple-option": // Added multiple-option schema
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.array(z.string()).min(1, `${field.label_name} is required`)
              : z.array(z.string()).optional();
          break;
        default:
          schemaFields[fieldId] = z.any().optional();
      }
    });
    return z.object(schemaFields);
  };

  const parseVisibleToData = (rawData) => {
    if (!rawData) return [];

    try {
      let parsed = rawData;

      if (typeof parsed === "string") {
        if (parsed.toLowerCase() === "all") {
          return [{ id: "0", label: "All", type: "all" }];
        }
        parsed = JSON.parse(parsed);
      }

      const dataArray = Array.isArray(parsed) ? parsed : [parsed];

      return dataArray.map((item) => ({
        id: String(item.id || item.value || ""),
        label: item.label || item.name || "",
        type: item.type || "user",
      }));
    } catch (err) {
      console.error("Error parsing visible_to:", err, rawData);
      return [];
    }
  };

  // 2. Initialize visible_to before useForm
  const initialVisibleTo = useMemo(() => {
    return parseVisibleToData(contactDetails?.visible_to);
  }, [contactDetails?.visible_to]);

  // groped state to hold the dynamic group data
  const [grouped, setGrouped] = useState([]);
  // ungroped state to hold the dynamic group data
  const [ungrouped, setUnGrouped] = useState([]);
  // state to store all dynamic fields
  const [dynamicFields, setDynamicFields] = useState([]);
  // Memoize the combined schema to avoid unnecessary recalculations
  const finalSchema = useMemo(() => {
    if (dynamicFields.length === 0) {
      return contactSchema;
    }

    // Combine grouped and ungrouped fields
    const allDynamicFields = [
      ...(grouped?.flatMap((g) => g.groupFields || []) || []),
      ...(ungrouped || []),
    ];

    if (allDynamicFields.length === 0) {
      return contactSchema;
    }

    const dynamicSchema = generateDynamicSchema(allDynamicFields);

    // Merge static and dynamic schemas
    return contactSchema.merge(dynamicSchema);
  }, [grouped, ungrouped, dynamicFields]);


  // sirf IDs extract
  // const visibleToIds = parsedVisibleTo.map(item => item.id);
  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    resolver: zodResolver(finalSchema),
    defaultValues: {
      ...contactDetails,
      visible_to: initialVisibleTo,
      countryId: (!contactDetails?.country || contactDetails?.country === "0") ? "99" : contactDetails?.country, // Default to India if no country
      state: contactDetails?.state,
      city: contactDetails?.city,
    },
  });
  console.log('countryId', watch('countryId'))

  // Update the visible_to state initialization
  const [visible_to, setVisibleTo] = useState(initialVisibleTo);


  // image state
  const [image, setImage] = useState(
    contactDetails?.contact_image !== "" ? contactDetails?.contact_image : null
  );
  const navigate = useNavigate();

  // required fields state
  const [showRequired, setShowRequired] = useState(true);
  // Helper function to check if field should be shown
  const shouldShowField = (field) => {
    if (!showRequired) return true; // Show all fields when toggle is off

    // When toggle is on, show only fields that are required OR important
    return field.is_required === "yes" || field.is_important === "yes";
  };

  // emails
  const [emails, setEmails] = useState([{ email: "", email_type: "Office" }]);
  const phoneOptions = [
    { value: "Mobile", label: "Mobile" },
    { value: "Work", label: "Work" },
    { value: "Home", label: "Home" },
    { value: "Personal", label: "Personal" },
  ]
  const emailOptions = [
    { value: "Office", label: "Office" },
    { value: "Personal", label: "Personal" },
  ]

  // contacts
  const [contacts, setContacts] = useState([{ phone: "", phone_type: "Mobile" }]);
  // states for empty emails and contact
  const [emptyEmailIndices, setEmptyEmailIndices] = useState([]);
  const [emptyContactIndices, setEmptyContactIndices] = useState([]);
  // refs for scrolling
  const emailSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const tagSectionRef = useRef(null);
  // duplicacy with respect to the primary email and contact
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null);
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null);
  const debouncedEmail = useDebounce(emails && emails[0]?.email || "", 500);
  const debouncedPhone = useDebounce(contacts && contacts[0]?.phone || "", 500);
  // timezone state
  const [selectedTimezone, setSelectedTimezone] = useState();
  // country state
  const [country, setCountries] = useState([
    {
      countryName: "",
      countryId: (!contactDetails?.country || contactDetails?.country === "0") ? "99" : contactDetails?.country,
    },
  ]);
  // states state
  const [states, setStates] = useState([
    {
      stateName: "",
      stateCode: contactDetails?.state,
    },
  ]);
  // city state
  const [city, setCity] = useState([
    {
      cityName: "",
      cityCode: contactDetails?.city,
    },
  ]);
  // decision state
  const [decision, setDecision] = useState(
    contactDetails?.decision_maker === "Yes" ? true : false
  );
  // tags state
  const [tags, setTags] = useState(
    contactDetails?.tag ? JSON.parse(contactDetails?.tag) : []
  );

  const [teams, setTeams] = useState([
    {
      teamName: "",
      teamId: "",
    },
  ]);
  // company canvas state
  const [companyShow, setCompanyShow] = useState(false)
  // state for companies
  const [company, setCompany] = useState([])
  // autoselect state
  const [recentCompany, setRecentCompanyId] = useState({
    recentCompanyId: "",
    recentCompanyName: "",
  })

  // lead source state
  const [lead_source, setLeadSource] = useState([])
  const [recentSource, setRecentSource] = useState({
    recentSourceId: "",
    recentSourceName: "",
  })
  // state to open the lead source form
  const [sourceShow, setSourceShow] = useState(false)

  // company section ref
  const companyRef = useRef(null);

  const visibleToRef = useRef(null);
  // lead source section ref
  const leadSourceRef = useRef(null)

  // add email field handler
  const handleAddEmail = () => {
    setEmails([
      ...emails,
      {
        email: "",
        email_type: "Office",
      },
    ]);
  };

  // add contact field handler
  const handleAddContact = () => {
    setContacts([
      ...contacts,
      {
        phone: "",
        phone_type: "Mobile",
      },
    ]);
  };

  // delete email handler
  const handleDeleteEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    // Also remove from emptyEmailIndices if it was there
    setEmptyEmailIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  // delete contact handler
  const handleDeleteContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
    // Also remove from emptyContactIndices if it was there
    setEmptyContactIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  // adding email inputs to email state array
  const handleEmailChange = (field, value, index) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setEmails(newEmails);
    // If the email field is no longer empty, remove its index from emptyEmailIndices
    if (field === "email" && value.trim() !== "") {
      setEmptyEmailIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  // adding contact inputs to contact state array
  const handleContactChange = (field, value, index, details) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value, ...details };
    setContacts(newContacts);
    // If the phone field is no longer empty, remove its index from emptyContactIndices
    if (field === "phone" && value.trim() !== "") {
      setEmptyContactIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  const validatePhoneNumber = (rawNumber, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        String(rawNumber),
        String(countryCode?.toUpperCase())
      );
      const valid = phoneNumber?.isValid() || false;
      return valid;
    } catch (error) {
      return false;
    }
  };

  // tags handler
  const handleTagChange = (newTags) => {
    const uniqueTags = [];

    newTags.forEach((tag) => {
      const lowerTag = tag.toLowerCase();

      const isDuplicate = uniqueTags.some((t) => t.toLowerCase() === lowerTag);
      if (!isDuplicate) {
        uniqueTags.push(tag);
      }
    });

    setTags(uniqueTags);
  };

  // useeffect for country
  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();
        if (!data) return;
        // Update state once with all country data
        setCountries(
          data.map((country) => ({
            label: country.name,
            value: country.id,
          }))
        );
      } catch (error) {
        //shoservererror()()
        return;
      }
    })();
  }, []);

  // useeffect for state
  const countryId = watch("countryId");
  const stateId = watch("state");
  useEffect(() => {
    (async () => {
      try {
        if (dirtyFields.countryId) {
          setValue("state", "");
          setValue("city", ""); // Also clear city when country changes
        }
        const data = await getStates(countryId);
        if (!data || data === undefined) {
          setStates([]);
          setCity([]); // Clear cities when no states
          setValue("state", "");
          setValue("city", "");
          return;
        }
        if (stateId === " " || stateId === "0") setValue("state", "");
        // Update state once with all country data
        const formattedStates = data.map((state) => ({
          stateName: state.state_name,
          stateCode: state.id,
        }))
        setStates(formattedStates);
        // Clear cities when states change
        if (dirtyFields?.state) {
          setCity([]);
          setValue("city", "");
        }
      } catch (error) {
        toast.error("Error Fetching State");
        return;
      }
    })();
  }, [countryId]);

  // city useeffect
  useEffect(() => {
    (async () => {
      try {
        if (!stateId || stateId === "0" || stateId === "") {
          setCity([]);
          setValue("city", ""); // Clear city selection
          return;
        }
        // Clear city when state changes
        if (dirtyFields.state) {
          setValue("city", "");
        }
        const data = await getCities(stateId);
        if (!data || data === undefined) {
          setCity([]);
          setValue("city", "");
          return;
        }
        // FIXED: Replace the cities array completely
        setCity(
          data.map((city) => ({
            cityName: city.city_name,
            cityCode: city.id,
          }))
        );
      } catch (error) {
        toast.error("Error Fetching Cities");
        return;
      }
    })();
  }, [stateId]);

  // common advance filter api calls that will be needed everywhere
  const { leadOwner } = useCommonData({
    leadOwner: true,
    leadSource: false,
    companies: false,
    contacts: false
  })

  // useEffetct fot fetching teams data
  useEffect(() => {
    (async () => {
      try {
        const res = await GetRequest(
          `/users/users/fetchAllTeams/${customer.customer_details.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        );
        if (res.err) {
          // toast.error("Error Fetching Data");
          return
        }
        const teamsData =
          res.data &&
          res.data.map((team) => ({
            teamName: team.team_name,
            teamId: team.id,
          }));
        setTeams(teamsData);
      } catch (error) {
        //shoservererror()()
        return
      }
      // if (teams && teams.length > 0) {
      //   setValue("owner_id", customer.customer_details?.id);
      // }
    })();
  }, []);

  // useeffect for companies
  useEffect(() => {
    ; (async () => {
      try {
        const res = await GetRequest(
          `company/fetchCompanies/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        )
        if (!res) return
        if (res.success === false) {
          return
        }
        setCompany(
          res.data?.map((c) => ({
            company_name: c.company_name,
            company_id: c.id,
          }))
        )
        // setting the default value for the recent company being addded
        res.data &&
          res.data.find((c) => {
            if (c.id === recentCompany.recentCompanyId) {
              setValue("company_id", recentCompany.recentCompanyId)
              setRecentCompanyId((prev) => ({
                ...prev,
                recentCompanyName: c.company_name,
              }))
            }
          })
      } catch (error) {
        //shoservererror()()
        return
      }
    })()
    return () => { }
  }, [recentCompany.recentCompanyId])

  // useeffect for lead source
  useEffect(() => {
    ; (async () => {
      try {
        const res = await GetRequest(
          `masters/getAllLead_source/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        )
        if (!res) return
        if (res.success === false) {
          return
        }
        setLeadSource(
          res.data?.map((lead) => ({
            source_title: lead.source_title,
            lead_source_id: lead.id,
          }))
        )
        res.data &&
          res.data.find((c) => {
            if (c.id === String(recentSource.recentSourceId)) {
              setValue("lead_source_id", recentSource.recentSourceId)
              setRecentSource((prev) => ({
                ...prev,
                recentSourceName: c.source_title,
              }))
            }
          })
      } catch (error) {
        //shoservererror()()
        return
      }
    })()
  }, [recentSource.recentSourceId])

  // Effect for email duplication check
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail) {
        setDuplicateEmailMessage(null);
        const isEmailExisting = await checkDuplicate({
          col: "email",
          customer_id: customer.customer_details?.customer_id, // Hardcoded as per example
          validating_val: debouncedEmail,
          contact_id: "", // Hardcoded as per example
          checkFor: "contacts",
          existing_val: contactDetails?.email || "",
        });
        if (isEmailExisting) {
          setDuplicateEmailMessage("Email already exists!");
        }
      } else {
        setDuplicateEmailMessage(null);
      }
    };
    checkEmail();
  }, [debouncedEmail]);

  // Effect for phone duplication check
  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone) {
        setDuplicatePhoneMessage(null);
        const isPhoneExisting = await checkDuplicate({
          col: "phone",
          customer_id: customer.customer_details?.customer_id,
          validating_val: debouncedPhone,
          contact_id: "",
          checkFor: "contacts",
          existing_val: contactDetails?.phone || "",
        });
        if (isPhoneExisting) {
          setDuplicatePhoneMessage("Phone number already exists!");
        }
      } else {
        setDuplicatePhoneMessage(null);
      }
    };
    checkPhone();
  }, [debouncedPhone]);

  // fetching dynamic fields for the contact form

  // Function to safely parse the visible_to string
  const parseVisibleTo = (val) => {
    try {
      if (typeof val === "string") return JSON.parse(val);
      return val || [];
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (!contactDetails) return

    const initDynamicFields = async () => {
      setIsDataFetching(true)
      try {
        const res = await GetRequest(
          `contacts/fetchCustomFields/${customer.customer_details?.customer_id}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate }
        );
        if (res.status === "success") {
          const allFields = [
            ...(res.data?.grouped?.flatMap((g) => g.groupFields || []) || []),
            ...(res.data?.ungrouped || []),
          ];

          setGrouped(res.data.grouped || []);
          setUnGrouped(res.data.ungrouped || []);
          setDynamicFields(allFields);

          const dynamicFieldValues = {};
          if (contactDetails?.custom_fields) {
            contactDetails?.custom_fields.forEach((cf) => {
              try {
                dynamicFieldValues[cf.field_id] = JSON.parse(cf.value);
              } catch {
                dynamicFieldValues[cf.field_id] = cf.value;
              }
            });
          }

          //  Set visible_to properly
          const parsedVisibleTo = parseVisibleToData(contactDetails?.visible_to);
          setValue("visible_to", parsedVisibleTo);
          setVisibleTo(parsedVisibleTo);

          const parsedEmails = contactDetails?.user_email && JSON.parse(contactDetails?.user_email)
          setEmails(parsedEmails?.length === 0 || !parsedEmails ? [{ email: "", email_type: "Office" }] : parsedEmails)

          const parsedPhone = contactDetails?.user_phone && JSON.parse(contactDetails?.user_phone)
          setContacts(parsedPhone?.length === 0 || !parsedPhone ? [{ phone: "", phone_type: "Mobile" }] : parsedPhone)

          const timeZone = contactDetails?.timezone ? JSON.parse(contactDetails?.timezone) : {
            value: "Asia/Kolkata",
            label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
            offset: 5.5,
            abbrev: "IST",
            altName: "India Standard Time",
          }
          setSelectedTimezone(timeZone)
          setTags(contactDetails?.tag ? JSON.parse(contactDetails.tag) : [])
          setDecision(contactDetails?.decision_maker === "Yes")
          setImage(contactDetails?.contact_image !== "" ? contactDetails.contact_image : null)
          reset({
            ...contactDetails,
            salutation: contactDetails?.salutation || "Mr.",
            owner_id: contactDetails?.owner_id,
            visible_to: parsedVisibleTo,
            company_id: contactDetails?.company_id,
            lead_source_id: contactDetails?.lead_source_id,
            countryId: (!contactDetails?.country || contactDetails?.country === "0") ? "99" : contactDetails?.country,
            state: contactDetails?.state_id,
            city: contactDetails?.city_id,
            report_to: contactDetails?.report_to,
            ...dynamicFieldValues,
          })

        }
      } catch (error) {
        console.error("Initialization error:", error);
        return
      } finally {
        setIsDataFetching(false)
      }
    };

    initDynamicFields();
  }, [contactDetails]);

  console.log('watch', watch())

  const handleClose = () => {
    if (isDataFetching) return;
    // Reset React Hook Form fields
    reset();
    // setContactData(null)
    setEmptyContactIndices([]);
    setEmptyEmailIndices([]);
    onHide();
    setActiveCanvas && setActiveCanvas(null)
  }

  // react query handler for submitting data
  const { mutate, isLoading } = usePostRequest(
    "/contacts/updateContact",
    true,
    { headers },
    { customer, setCustomer, rf: headers.Authentication, navigate }
  );

  // final submit handler
  const handleFormSubmit = async (formData) => {
    // Checking empty email states and contact states before form submitting
    setLoading(true)
    const newEmptyEmailIndices = emails.reduce((acc, email, index) => {
      if (!email.email || email.email.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);
    const newEmptyContactIndices = contacts.reduce((acc, contact, index) => {
      if (!contact.phone || contact.phone.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);
    setEmptyEmailIndices(newEmptyEmailIndices);
    setEmptyContactIndices(newEmptyContactIndices);
    // scrolling if the email or contact input is empty
    if (newEmptyEmailIndices.length > 0 || newEmptyContactIndices.length > 0) {
      if (newEmptyEmailIndices.length > 0) {
        emailSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (newEmptyContactIndices.length > 0) {
        contactSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      setLoading(false)
      return;
    }

    const custom_fields = [];
    const staticFieldsPayload = { ...formData }; // Start with all form data
    dynamicFields.forEach((field) => {
      const fieldName = field.id;
      if (fieldName && formData.hasOwnProperty(fieldName)) {
        let formattedValue = formData[fieldName];
        let isRangeField = false; // Initialize flag for range property

        // Handle complex types for custom_fields payload
        if (
          field.label_type === "date range" ||
          field.label_type === "time-range"
        ) {
          isRangeField = true; // Set flag for range fields
          // Ensure both start and end are present before sending
          if (
            !formattedValue ||
            (!formattedValue.start && !formattedValue.end)
          ) {
            formattedValue = { start: "", end: "" }; // Send empty object if range is incomplete/empty
          }
        } else if (field.label_type === "multiple-option") {
          // Ensure it's an array
          if (!Array.isArray(formattedValue)) {
            formattedValue = []; // Send empty array if not an array
          }
        }

        const customFieldEntry = {
          field_id: field.id.toString(), // Ensure ID is a string
          value: formattedValue || "",
          range: isRangeField ? true : false,
        };
        custom_fields.push(customFieldEntry);

        // Remove dynamic field from static payload to avoid duplication
        delete staticFieldsPayload[fieldName];
      }
    });
    const dataToSend = {
      ...formData,
      contact_id: contactDetails.id,
      country: parseInt(formData.countryId || "99"),
      state: parseInt(formData.state || "0"),
      city: parseInt(formData.city || "0"),
      customer_id: customer.customer_details.customer_id,
      decision_maker: !decision ? "No" : "Yes",
      email: emails[0].email,
      phone: contacts[0].phone,
      user_email: emails,
      user_phone: contacts,
      tag: tags.length < 1 ? [] : tags,
      timezone: selectedTimezone,
      contact_image: image && typeof image === "object" ? image[0] : null,
      old_contact_image: contactDetails.contact_image,
      // visible_to: formData.owner_id,
      report_to: formData.report_to,
    };

    const finalPayload = {
      user_login_id: customer.customer_details?.id,
      user_id: customer.customer_details?.id,
      custom_fields,
      ...dataToSend,
      updating_field: getUpdatedFields(contactDetails, dataToSend),
      // formdata by default also contains the default visible to values so there's no harm if i directly assign formdata values
      visible_to: formData.visible_to
    };

    mutate(finalPayload, {
      onSuccess: (data) => {
        if (data.status === "fail") {
          toast.error(data.message);
          return;
        }
        refetch && refetch();
        onHide && onHide();
        toast.success(data.message);
        setLoading(false)
        // setShowContactDetailsCanvas && setShowContactDetailsCanvas(false);
        fetchContactDetails && fetchContactDetails()
        return;
      },
    });
  };

  return (
    <div className="d-flex w-100">
      <ContactFormCommonOffcanvas
        loading={loading}
        isDataFetching={isDataFetching}
        formTitle={"Edit Contact"}
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        showRequired={showRequired}
        setShowRequired={setShowRequired}

        // handler from react hook form
        handleSubmit={handleSubmit}
        register={register}
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}

        // handler for actual form submit
        handleFormSubmit={handleFormSubmit}
        duplicateEmailMessage={duplicateEmailMessage}
        duplicatePhoneMessage={duplicatePhoneMessage}

        emails={emails}
        handleAddContact={handleAddContact}
        handleAddEmail={handleAddEmail}
        handleDeleteContact={handleDeleteContact}
        handleDeleteEmail={handleDeleteEmail}
        handleEmailChange={handleEmailChange}
        handleContactChange={handleContactChange}

        phoneOptions={phoneOptions}
        emailOptions={emailOptions}
        emptyEmailIndices={emptyEmailIndices}
        emptyContactIndices={emptyContactIndices}
        emailSectionRef={emailSectionRef}
        visibleToRef={visibleToRef}
        leadSourceRef={leadSourceRef}
        contactSectionRef={contactSectionRef}

        company={company}
        contacts={contacts}
        leadOwner={leadOwner}
        country={country}
        states={states}
        city={city}
        teams={teams}
        ungrouped={ungrouped}
        grouped={grouped}
        lead_source={lead_source}

        validatePhoneNumber={validatePhoneNumber}
        companyShow={companyShow}
        setCompanyShow={setCompanyShow}
        decision={decision}
        setDecision={setDecision}
        selectedTimezone={selectedTimezone}
        setSelectedTimezone={setSelectedTimezone}
        sourceShow={sourceShow}
        setSourceShow={setSourceShow}

        tags={tags}
        shouldShowField={shouldShowField}
        allContacts={allContacts}
        // masterGrant={masterGrant}
        // companyGrants={companyGrants}

        handleTagChange={handleTagChange}
      />
      {companyShow &&
        <CreateCompany
          show={companyShow}
          setShow={setCompanyShow}
          contactsModule={true}
          setRecentCompanyId={setRecentCompanyId}
          setContactShow={() => setShow(true)}
        ></CreateCompany>
      }
      {sourceShow &&
        <UsersPopup
          setRecentSourceId={setRecentSource}
          sourceShow={sourceShow}
          setSourceShow={setSourceShow}
        />
      }
    </div>
  );
};